import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { GraduationCap, CheckCircle2, Lock } from "lucide-react";

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 6) return setErr("La contraseña debe tener al menos 6 caracteres");
    if (pw !== pw2) return setErr("Las contraseñas no coinciden");
    if (!token) return setErr("Falta el token de recuperación");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/login"), 2200);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="hidden lg:flex bg-marino text-white p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'url("https://images.unsplash.com/photo-1556761175-4b46a572b786?w=1200")', backgroundSize: 'cover' }} />
        <div className="relative flex items-center gap-3">
          <img src="/shield-ompec.svg" alt="OM-PEC" className="h-14 w-auto" />
          <div>
            <span className="font-heading font-black text-2xl block">OM-PEC</span>
            <span className="text-[10px] uppercase tracking-wider text-naranja font-semibold">Protegemos · Fortalecemos</span>
          </div>
        </div>
        <div className="relative">
          <h2 className="font-heading font-black text-4xl tracking-tight leading-tight">Nueva contraseña</h2>
          <p className="mt-3 text-white/80 max-w-md">Elige una contraseña segura para tu cuenta.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10 bg-white">
        <div className="w-full max-w-md">
          {done ? (
            <div data-testid="reset-done" className="text-center animate-fade-in">
              <div className="w-14 h-14 rounded-full bg-green-100 text-green-700 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h1 className="font-heading font-black text-2xl text-marino">¡Contraseña actualizada!</h1>
              <p className="mt-3 text-marino/70">Redirigiendo al inicio de sesión...</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <div>
                <h1 className="font-heading font-black text-3xl text-marino tracking-tight">Establece una nueva contraseña</h1>
                <p className="mt-1 text-marino/70 text-sm">Mínimo 6 caracteres.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-marino mb-1">Nueva contraseña</label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-gris absolute left-3 top-1/2 -translate-y-1/2" />
                  <input data-testid="reset-pw" type="password" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} className="w-full pl-9 pr-4 py-3 border border-gris rounded-lg focus:border-marino focus:outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-marino mb-1">Confirma contraseña</label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-gris absolute left-3 top-1/2 -translate-y-1/2" />
                  <input data-testid="reset-pw2" type="password" required minLength={6} value={pw2} onChange={(e) => setPw2(e.target.value)} className="w-full pl-9 pr-4 py-3 border border-gris rounded-lg focus:border-marino focus:outline-none" />
                </div>
              </div>
              {err && <div data-testid="reset-error" className="text-naranja text-sm bg-naranja/10 border border-naranja/30 rounded-lg p-3">{err}</div>}
              <button disabled={loading} data-testid="reset-submit" className="w-full bg-naranja text-white py-3 rounded-lg font-semibold hover:bg-naranjaHover disabled:opacity-60">
                {loading ? "Actualizando..." : "Guardar nueva contraseña"}
              </button>
              <p className="text-sm text-marino/70 text-center">
                <Link to="/login" className="text-naranja font-medium">← Volver a iniciar sesión</Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
